import { useState } from 'react';

import {
  AddNewDeviceCard,
  ChakraFlex,
  DateFormat,
  DeviceInformationTrackingCard,
  Text,
} from '@equip.health/ui';
import {
  EquipScaleOrderStatus,
  ORDER_PENDING_CONFIRMATION_TEXT,
  ORDERED_TEXT,
  OrderEquipScaleFrom,
  REORDER_EQUIP_SCALE,
  ReorderContext,
} from '~/lib/constants/patients.constants';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { EquipScaleWarning } from '~/lib/hooks/useEquipScaleWarnings';
import {
  getActiveOrder,
  getDeviceImei,
  getDeviceOrderDate,
  getDeviceStatus,
  getDeviceStatusForActive,
  getStatusHistoryDateString,
  isOrderCanceledOrFailed,
  isOrderPendingConfirmation,
} from '~/lib/util/bodyTraceTracking.util';
import { formattedDateFromISO } from '~/lib/util/date.util';
import { useGetBodyTraceOrdersQuery } from '~/store/services/clinicalData';
import { useFlags } from 'launchdarkly-react-client-sdk';

import EquipScaleTrackingModal from './EquipScaleTrackingModal';
import ReorderEquipScaleModal from './ReorderEquipScaleModal';
import { PatientClinicalEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';

interface ClinicalProfileDevicesSectionProps {
  patientId: string;
  patientName: string;
  handleOrderScale: (
    from: OrderEquipScaleFrom,
    reorderContext?: ReorderContext,
  ) => void;
  warnings?: EquipScaleWarning[];
}

const ORDER_EQUIP_SCALE = 'order-equip-scale';

const ClinicalProfileDevicesSection = ({
  patientId,
  patientName,
  handleOrderScale,
  warnings = [],
}: ClinicalProfileDevicesSectionProps) => {
  const [shouldShowTrackingModal, setShouldShowTrackingModal] =
    useState<boolean>(false);
  const [shouldShowReorderModal, setShouldShowReorderModal] =
    useState<boolean>(false);

  const { data: bodyTraceOrderData, isLoading } =
    useGetBodyTraceOrdersQuery(patientId);
  const { trackEvent } = useAnalytics();
  const { reorderEquipScale: isReorderEquipScaleEnabled } = useFlags();
  const { permissions } = useGlobalContext();

  const canReorderEquipScale =
    isReorderEquipScaleEnabled &&
    permissions[MaudPermissions.MAUD_REORDER_EQUIP_SCALE];

  const activeOrder = getActiveOrder(bodyTraceOrderData);

  const hasOrderData = !!activeOrder;

  const formattedDate = activeOrder && getStatusHistoryDateString(activeOrder);
  const deviceStatus =
    getDeviceStatusForActive(activeOrder) || getDeviceStatus(activeOrder);

  const deviceOrderDate =
    activeOrder && getDeviceOrderDate(activeOrder, formattedDate);
  const deviceImei = activeOrder && getDeviceImei(activeOrder);

  const activeDeviceStatus = getDeviceStatusForActive(activeOrder);
  const status: DeviceOrderStatus = isOrderCanceledOrFailed(deviceStatus)
    ? 'Error'
    : deviceStatus;
  const orderStatus = activeOrder && activeOrder.status.label;

  let orderDate: string;

  if (isOrderPendingConfirmation(activeOrder)) {
    orderDate =
      activeDeviceStatus === EquipScaleOrderStatus.ACTIVE
        ? ORDERED_TEXT
        : ORDER_PENDING_CONFIRMATION_TEXT;
    if (activeDeviceStatus === EquipScaleOrderStatus.ACTIVE) {
      orderDate = activeOrder?.device?.dateActive
        ? `Activated on ${formattedDateFromISO(activeOrder.device.dateActive, DateFormat.shortMonthDayYear)}`
        : 'Activated';
    } else if (orderStatus === EquipScaleOrderStatus.DELIVERED) {
      orderDate = EquipScaleOrderStatus.DELIVERED;
    } else if (orderStatus === EquipScaleOrderStatus.SHIPPED) {
      orderDate = EquipScaleOrderStatus.SHIPPED;
    } else if (orderStatus === EquipScaleOrderStatus.REQUESTED) {
      orderDate = ORDER_PENDING_CONFIRMATION_TEXT;
    } else if (orderStatus === EquipScaleOrderStatus.CONFIRMED) {
      orderDate = ORDERED_TEXT;
    } else {
      orderDate = 'Error';
    }
  } else {
    orderDate = deviceOrderDate;
  }

  const handleOnClick = () => {
    trackEvent(PatientClinicalEvents.deviceCardOrderScaleClicked, {
      source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
    });
    handleOrderScale(OrderEquipScaleFrom.DEVICE);
  };

  const handleReorderContinue = (reasonCode: string) => {
    setShouldShowReorderModal(false);
    handleOrderScale(OrderEquipScaleFrom.DEVICE, {
      isReorder: true,
      reorderReasonCode: reasonCode,
    });
  };

  const kebabItems = canReorderEquipScale
    ? [
        {
          label: REORDER_EQUIP_SCALE,
          onClick: () => {
            trackEvent(
              PatientClinicalEvents.equipScaleReorderScaleMenuOptionClicked,
              {
                source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
              },
            );
            setShouldShowReorderModal(true);
          },
        },
      ]
    : [];

  return (
    <>
      {shouldShowTrackingModal && (
        <EquipScaleTrackingModal
          bodyTraceOrderData={activeOrder}
          isLoading={isLoading}
          isOpen={shouldShowTrackingModal}
          onClose={() => setShouldShowTrackingModal(false)}
        />
      )}

      <ReorderEquipScaleModal
        isOpen={shouldShowReorderModal}
        onClose={() => setShouldShowReorderModal(false)}
        onContinue={handleReorderContinue}
        warnings={warnings}
      />

      <ChakraFlex flexDirection="column" gap="16px">
        <Text color="neutral.primary" variant="h5">
          Devices
        </Text>
        {hasOrderData ? (
          <DeviceInformationTrackingCard
            id={`${ORDER_EQUIP_SCALE}__device-tracking-card`}
            imei={deviceImei}
            kebabItems={kebabItems}
            onKebabMenuOpen={() => {
              trackEvent(
                PatientClinicalEvents.equipScaleDeviceCardKebabMenuClicked,
                {
                  source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
                },
              );
            }}
            onClick={() => {
              trackEvent(PatientClinicalEvents.deviceCardClicked, {
                source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
              });
              setShouldShowTrackingModal(true);
            }}
            orderDate={orderDate}
            status={status}
            title="Equip scale"
          />
        ) : (
          <AddNewDeviceCard
            body={`${patientName} doesn't have a scale yet`}
            id={`${ORDER_EQUIP_SCALE}__add-device-card`}
            onClick={handleOnClick}
            title="Equip scale"
          />
        )}
      </ChakraFlex>
    </>
  );
};

export default ClinicalProfileDevicesSection;
